import React from 'react';
import { useStyleInjection, useOnlineStatus } from '../../hooks';
import { useQuestionDetail } from '../../hooks/useQuestionDetail';
import { ReplyList } from './ReplyList';
import { RfiStillNeeded } from './RfiStillNeeded';
import { ReplyForm } from '../ReplyForm';
import { QuestionDetailSkeleton } from './QuestionDetailSkeleton';
import { ImageThumbnailGrid } from '../ImageThumbnailGrid/ImageThumbnailGrid';
import { OfflineNotice } from '../OfflineNotice';
import { formatTimeAgo } from '../../lib/formatTimeAgo';
import { BREAKPOINTS } from '../../tokens';
import { EDITORIAL_CORRECTION_PRESENTATIONS } from '@find-my-part/types';

export interface QuestionDetailProps {
  questionId: string;
  onBack: () => void;
  className?: string;
}

export function QuestionDetail({
  questionId,
  onBack,
  className = '',
}: QuestionDetailProps): React.ReactElement {
  useStyleInjection(detailStyles, 'fmp-detail-styles');
  const isOnline = useOnlineStatus();

  const { question, replies, rfi, isLoading, error, refetch } = useQuestionDetail(questionId);

  // Derive solved state from replies since the detail endpoint doesn't include hasSolution
  const hasSolution = replies.some((r) => r.isSolution);

  const handleBackKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onBack();
    }
  };

  if (isLoading) {
    return (
      <div className={`fmp-detail ${className}`}>
        <button
          type="button"
          className="fmp-detail__back"
          onClick={onBack}
          onKeyDown={handleBackKeyDown}
          aria-label="Back to questions list"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            className="fmp-detail__back-icon"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to questions
        </button>
        <QuestionDetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`fmp-detail ${className}`}>
        <button
          type="button"
          className="fmp-detail__back"
          onClick={onBack}
          aria-label="Back to questions list"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            className="fmp-detail__back-icon"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to questions
        </button>
        {!isOnline ? (
          <OfflineNotice />
        ) : (
          <div className="fmp-detail__error" role="alert">
            <p className="fmp-detail__error-message">
              {error.includes('NOT_FOUND') || error.includes('not found')
                ? 'Question not found.'
                : 'Something went wrong. Please try again.'}
            </p>
          </div>
        )}
        <div className="fmp-detail__error-actions">
          <button
            type="button"
            onClick={refetch}
            className="fmp-btn fmp-btn--secondary"
            aria-label="Retry loading question"
            disabled={!isOnline}
          >
            {isOnline ? 'Try Again' : 'Waiting for connection...'}
          </button>
        </div>
      </div>
    );
  }

  if (!question) return <div className={`fmp-detail ${className}`} />;

  return (
    <div className={`fmp-detail ${className}`}>
      <button
        type="button"
        className="fmp-detail__back"
        onClick={onBack}
        aria-label="Back to questions list"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className="fmp-detail__back-icon"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to questions
      </button>

      <article className="fmp-detail__question">
        <header className="fmp-detail__header">
          <h2 className="fmp-detail__title">{question.title}</h2>
          <div className="fmp-detail__meta">
            <span className="fmp-detail__author">Asked by {question.posterName}</span>
            <span className="fmp-detail__separator" aria-hidden="true">
              &middot;
            </span>
            <time dateTime={new Date(question.createdAt).toISOString()}>
              {formatTimeAgo(question.createdAt)}
            </time>
            {question.editoriallyCorrected && (
              <span className="fmp-editorial-marker">
                {EDITORIAL_CORRECTION_PRESENTATIONS.CUSTOMER_OR_GUEST}
              </span>
            )}
            {hasSolution && (
              <span className="fmp-solution-badge">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden="true"
                  className="fmp-solution-badge__icon"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Solved
              </span>
            )}
          </div>
        </header>

        <div className="fmp-detail__body">
          <p className="fmp-detail__description">{question.description}</p>

          <ImageThumbnailGrid
            images={question.images}
            labelNoun="Question image"
            groupLabel="Question images"
          />
        </div>

        <RfiStillNeeded rfi={rfi} />

        <ReplyList replies={replies} />
        <ReplyForm
          questionId={question.questionId}
          defaultCollapsed={hasSolution || replies.length >= 3}
        />
      </article>
    </div>
  );
}

const detailStyles = `
  :where(.fmp-widget-scope) .fmp-detail {
    max-inline-size: var(--fmp-size-600);
    margin-inline: auto;
  }

  :where(.fmp-widget-scope.fmp-widget-scope--hub-layout-v1) .fmp-detail {
    inline-size: 100%;
    max-inline-size: none;
    margin-inline: 0;
  }

  :where(.fmp-widget-scope) .fmp-detail__back {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-xs);
    padding: var(--fmp-space-sm) 0;
    margin-bottom: var(--fmp-space-lg);
    border: none;
    background: none;
    color: var(--fmp-color-primary);
    font-size: var(--fmp-font-size-base);
    font-family: inherit;
    cursor: pointer;
    transition: none;
  }

  :where(.fmp-widget-scope) .fmp-detail__back:hover {
    color: var(--fmp-color-primary-hover);
  }

  :where(.fmp-widget-scope) .fmp-detail__back:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
    border-radius: var(--fmp-radius-sm);
  }

  :where(.fmp-widget-scope) .fmp-detail__back-icon {
    width: var(--fmp-size-16);
    height: var(--fmp-size-16);
  }

  :where(.fmp-widget-scope) .fmp-detail__question {
    background: var(--fmp-color-surface);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    overflow: hidden;
  }

  :where(.fmp-widget-scope) .fmp-detail__header {
    padding: var(--fmp-space-xl) var(--fmp-space-xl) 0;
  }

  :where(.fmp-widget-scope) .fmp-detail__title {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0 0 var(--fmp-space-sm);
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-detail__meta {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-text-muted);
    flex-wrap: wrap;
  }

  :where(.fmp-widget-scope) .fmp-detail__author {
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-hint);
  }

  :where(.fmp-widget-scope) .fmp-detail__separator {
    color: var(--fmp-color-border-light);
  }

  :where(.fmp-widget-scope) .fmp-editorial-marker {
    font-style: italic;
  }

  :where(.fmp-widget-scope) .fmp-detail__meta > .fmp-solution-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-xs);
    white-space: nowrap;
  }

  :where(.fmp-widget-scope) .fmp-detail__meta > .fmp-solution-badge .fmp-solution-badge__icon {
    inline-size: var(--fmp-size-14);
    block-size: var(--fmp-size-14);
    flex: none;
  }

  :where(.fmp-widget-scope) .fmp-detail__body {
    padding: var(--fmp-space-lg) var(--fmp-space-xl);
  }

  :where(.fmp-widget-scope) .fmp-detail__description {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-text-secondary);
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  :where(.fmp-widget-scope) .fmp-detail__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--fmp-size-40) var(--fmp-space-xl);
    text-align: center;
  }

  :where(.fmp-widget-scope) .fmp-detail__error-message {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-detail__error-actions {
    display: flex;
    justify-content: center;
    margin-top: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-reply-list {
    padding: var(--fmp-space-lg) var(--fmp-space-xl) var(--fmp-space-xl);
    border-top: var(--fmp-border-width) solid var(--fmp-color-border);
  }

  :where(.fmp-widget-scope) .fmp-reply-list__heading {
    margin: 0 0 var(--fmp-space-lg);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-reply-list__empty {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    font-style: italic;
  }

  :where(.fmp-widget-scope) .fmp-reply-list__items {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-md);
  }

  /* Elevated best answer section */
  :where(.fmp-widget-scope) .fmp-best-answer {
    margin-bottom: var(--fmp-space-lg);
    padding: var(--fmp-space-lg);
    background: var(--fmp-color-success-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-success-border);
    border-radius: var(--fmp-radius-surface);
  }

  :where(.fmp-widget-scope) .fmp-best-answer__heading {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    margin: 0 0 var(--fmp-space-md);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-best-answer__icon {
    width: var(--fmp-size-18);
    height: var(--fmp-size-18);
    flex-shrink: 0;
  }

  :where(.fmp-widget-scope) .fmp-best-answer .fmp-reply {
    border-color: var(--fmp-color-success-border);
    background: var(--fmp-color-surface);
  }

  :where(.fmp-widget-scope) .fmp-reply {
    padding: var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
    background: var(--fmp-color-surface-muted);
  }

  :where(.fmp-widget-scope) .fmp-reply--solution {
    border-color: var(--fmp-color-success-border);
    background: var(--fmp-color-surface);
  }

  :where(.fmp-widget-scope) .fmp-reply__solution-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-xs);
    margin-bottom: var(--fmp-space-sm);
    padding: var(--fmp-size-2) var(--fmp-space-sm);
    border-radius: var(--fmp-radius-sm);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-success);
    background: var(--fmp-color-success-bg);
  }

  :where(.fmp-widget-scope) .fmp-reply__solution-icon {
    width: var(--fmp-size-14);
    height: var(--fmp-size-14);
  }

  :where(.fmp-widget-scope) .fmp-reply__body {
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-text-secondary);
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  :where(.fmp-widget-scope) .fmp-reply__meta {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    margin-top: var(--fmp-space-sm);
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-reply__author {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-size-6);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-hint);
  }

  :where(.fmp-widget-scope) .fmp-reply__admin-badge {
    display: inline-block;
    padding: var(--fmp-size-1) var(--fmp-size-6);
    border-radius: var(--fmp-radius-sm);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
    background: var(--fmp-color-info-bg);
    color: var(--fmp-color-primary);
  }

  :where(.fmp-widget-scope) .fmp-reply__separator {
    color: var(--fmp-color-border-light);
  }

  @media screen and (max-width: ${BREAKPOINTS.viewport.smallMax}) {
    :where(.fmp-widget-scope) .fmp-detail__title {
      font-size: var(--fmp-font-size-lg);
    }

    :where(.fmp-widget-scope) .fmp-detail__header,
    :where(.fmp-widget-scope) .fmp-detail__body,
    :where(.fmp-widget-scope) .fmp-reply-list {
      padding-left: var(--fmp-space-lg);
      padding-right: var(--fmp-space-lg);
    }

    :where(.fmp-widget-scope) .fmp-reply {
      padding: var(--fmp-space-md);
    }

    :where(.fmp-widget-scope) .fmp-detail__back {
      min-height: var(--fmp-size-44);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-detail__back {
      transition: none;
    }
  }

  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-reply--solution {
      border-width: var(--fmp-border-width-strong);
    }

    :where(.fmp-widget-scope) .fmp-detail__back:focus-visible {
      outline-color: currentColor;
    }
  }
`;
